import { isEqual } from 'lodash';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  useAgentProfileCreateControllerAgentProfileCreateForUsersMutation,
  useAgentProfileEditControllerAgentProfileEditForUsersMutation,
  type AgentProfileResponseDto,
} from '@shared/services/apiService/apiService';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { copyJobWorkflow } from './copyJobWorkflow';
import { jobMetadata, type WorkspaceJobProfile } from './jobMetadata';
import { useGuidedCreationReceipt, type GuidedJobPlan } from './GuidedCreationReceiptProvider';

export type GuidedJobSetupHandle = {
  validate: () => boolean;
  resolve: () => Promise<AgentProfileResponseDto | null>;
  markAgentStarted: () => void;
  markAgentUnknown: () => void;
  complete: () => void;
};
const jobSnapshot = (profile: WorkspaceJobProfile) => ({
  id: profile.id,
  type: profile.type,
  name: profile.name,
  prompt: profile.prompt ?? '',
  firstMessage: profile.firstMessage ?? null,
  firstMessageInterruptionsEnabled: profile.firstMessageInterruptionsEnabled,
  taskFlow: profile.taskFlow ?? null,
  sector: profile.sector ?? null,
  summary: profile.summary ?? null,
});
const emptyPlan = (): GuidedJobPlan => ({
  mode: 'new',
  values: { name: '', prompt: '', firstMessage: '', firstMessageInterruptionsEnabled: false },
  taskFlow: null,
});

/** A real native Job is created only after the assistant review. Receipts fence later phases. */
export const GuidedJobSetup = forwardRef<
  GuidedJobSetupHandle,
  {
    sessionKey: string;
    campaignId: string;
    plan: GuidedJobPlan | null;
    onPlanChange: (plan: GuidedJobPlan | null) => void;
    source: WorkspaceJobProfile | null;
    disabled: boolean;
    busy: boolean;
    activeStep: boolean;
    savedJobField: ReactNode;
    canContinue: () => boolean;
    loadSource: (id: string) => Promise<AgentProfileResponseDto | undefined>;
    onBlockedChange: (blocked: boolean) => void;
    onReceiptChange: (id: string | null) => void;
  }
>(function GuidedJobSetup(
  {
    sessionKey,
    campaignId,
    plan,
    onPlanChange,
    source,
    disabled,
    busy,
    activeStep,
    savedJobField,
    canContinue,
    loadSource,
    onBlockedChange,
    onReceiptChange,
  },
  ref,
) {
  const { receipt, setReceipt, getReceipt } = useGuidedCreationReceipt(sessionKey);
  const [error, setError] = useState('');
  const [create] = useAgentProfileCreateControllerAgentProfileCreateForUsersMutation();
  const [edit] = useAgentProfileEditControllerAgentProfileEditForUsersMutation();
  const alive = useRef(true);
  const resolving = useRef(false);
  const access = useRef({ disabled, canContinue });
  access.current = { disabled, canContinue };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const storedPlan = receipt?.plan;
  useEffect(() => {
    if (storedPlan) onPlanChange(storedPlan);
  }, [storedPlan, onPlanChange]);
  useEffect(() => {
    onBlockedChange(
      !!receipt &&
        (receipt.pending ||
          ['job-unknown', 'job-changed', 'agent-pending', 'agent-unknown', 'completed'].includes(
            receipt.phase,
          )),
    );
    onReceiptChange(receipt?.profile?.id ?? null);
  }, [receipt, onBlockedChange, onReceiptChange]);
  const allowed = () => alive.current && !access.current.disabled && access.current.canContinue();
  const valid = () => {
    if (!plan) return true;
    const issue =
      !plan.values.name.trim() || plan.values.name.length > 120
        ? 'Use a Job title of 1–120 characters.'
        : !plan.values.prompt.trim()
          ? 'Conversation instructions are required for a new Job.'
          : '';
    setError(issue);
    return !issue;
  };
  const resolve = async () => {
    if (resolving.current || !allowed() || !plan || !valid()) return null;
    resolving.current = true;
    try {
      let current = getReceipt();
      if (
        current?.pending ||
        current?.phase === 'job-unknown' ||
        current?.phase === 'job-changed' ||
        current?.phase === 'agent-unknown' ||
        current?.phase === 'agent-pending' ||
        current?.phase === 'completed'
      )
        return null;
      if (!current) {
        if (plan.mode === 'copy') {
          try {
            const source = plan.sourceId ? await loadSource(plan.sourceId) : undefined;
            if (!allowed()) return null;
            if (
              !source ||
              source.id !== plan.sourceId ||
              source.type === 'system' ||
              !source.prompt?.trim()
            ) {
              setError(
                'The source Job is unavailable or its instructions cannot be copied. Use a current saved Job or create your own instructions.',
              );
              return null;
            }
          } catch {
            if (alive.current)
              setError(
                'The source Job could not be verified. Retry verification before creating a copy.',
              );
            return null;
          }
        }
        const workflow = plan.taskFlow ? copyJobWorkflow(plan.taskFlow) : null;
        current = {
          plan: structuredClone(plan),
          profile: null,
          workflow,
          workflowSaved: !workflow,
          phase: 'job-pending',
          pending: true,
          error: '',
        };
        if (!setReceipt(current)) return null;
        try {
          const profile = await create({
            agentProfileCreateRequestDto: {
              ...current.plan.values,
              name: current.plan.values.name.trim(),
              prompt: current.plan.values.prompt.trim(),
            },
          }).unwrap();
          current = {
            ...current,
            profile,
            pending: false,
            phase: current.workflowSaved ? 'ready' : 'workflow-pending',
          };
          // Store the acknowledged ID even after navigation, while this principal still owns it.
          if (!setReceipt(current)) return null;
        } catch (e) {
          const status = typeof e === 'object' && e && 'status' in e ? e.status : undefined;
          const definite =
            typeof status === 'number' && status >= 400 && status < 500 && status !== 408;
          const message = getApiErrorMessage(
            e,
            'Job creation was not confirmed. Inspect the Job library before starting a separate setup.',
          );
          if (definite) {
            setReceipt(null);
            if (alive.current) setError(message);
          } else setReceipt({ ...current, pending: false, phase: 'job-unknown', error: message });
          return null;
        }
      }
      if (!allowed() || !current.profile) return null;
      try {
        const live = await loadSource(current.profile.id);
        if (!allowed()) return null;
        const baseline = jobSnapshot(current.profile);
        const expected = jobSnapshot({ ...current.profile, taskFlow: current.workflow });
        if (
          !live ||
          live.type !== 'custom' ||
          (!isEqual(jobSnapshot(live), baseline) && !isEqual(jobSnapshot(live), expected))
        ) {
          setReceipt({
            ...current,
            phase: 'job-changed',
            pending: false,
            error:
              'The saved Job changed or is unavailable after review. Inspect the exact Job and start a separate setup using its reviewed saved configuration. This attempt will not create an agent.',
          });
          return null;
        }
        if (!current.workflowSaved && isEqual(jobSnapshot(live), expected)) {
          current = {
            ...current,
            profile: live,
            workflowSaved: true,
            phase: 'ready',
            pending: false,
            error: '',
          };
          if (!setReceipt(current)) return null;
        }
      } catch {
        setReceipt({
          ...current,
          pending: false,
          error:
            'The current saved Job could not be verified. Confirm again to retry verification of this same Job ID.',
        });
        return null;
      }
      if (!current.workflowSaved) {
        current = { ...current, pending: true, phase: 'workflow-pending', error: '' };
        if (!setReceipt(current)) return null;
        try {
          const profile = await edit({
            profileId: current.profile!.id,
            agentProfileEditRequestDto: { taskFlow: current.workflow },
          }).unwrap();
          if (
            !isEqual(
              jobSnapshot(profile),
              jobSnapshot({ ...current.profile!, taskFlow: current.workflow }),
            )
          ) {
            setReceipt({
              ...current,
              phase: 'job-changed',
              pending: false,
              error:
                'The saved Job changed during workflow copying. Inspect the exact Job and start a separate setup using its reviewed saved configuration.',
            });
            return null;
          }
          current = { ...current, profile, workflowSaved: true, pending: false, phase: 'ready' };
          if (!setReceipt(current)) return null;
        } catch (e) {
          setReceipt({
            ...current,
            pending: false,
            error: getApiErrorMessage(
              e,
              'The Job exists, but its workflow save was not confirmed. Confirm again to retry the same Job ID.',
            ),
          });
          return null;
        }
      }
      return allowed() ? current.profile : null;
    } finally {
      resolving.current = false;
    }
  };
  useImperativeHandle(ref, () => ({
    validate: valid,
    resolve,
    markAgentStarted: () => {
      const current = getReceipt();
      if (current?.profile && current.workflowSaved)
        setReceipt({ ...current, phase: 'agent-pending', pending: true, error: '' });
    },
    markAgentUnknown: () => {
      const current = getReceipt();
      if (current)
        setReceipt({
          ...current,
          phase: 'agent-unknown',
          pending: false,
          error:
            'Agent creation was not confirmed. Inspect this campaign before starting another setup; this POST will not be replayed.',
        });
    },
    complete: () => {
      const current = getReceipt();
      if (current) setReceipt({ ...current, phase: 'completed', pending: false, error: '' });
    },
  }));
  const locked = disabled || busy || !!receipt;
  const mode = plan?.mode || 'saved';
  const canCopy = !!source && source.type !== 'system' && !!source.prompt?.trim();
  const changeMode = (value: string) => {
    if (locked) return;
    setError('');
    if (value === 'saved') onPlanChange(null);
    else if (value === 'new') onPlanChange(emptyPlan());
    else if (canCopy && source)
      onPlanChange({
        mode: 'copy',
        sourceId: source.id,
        values: {
          ...jobMetadata(source),
          name: source.name,
          prompt: source.prompt!,
          firstMessage: source.firstMessage,
          firstMessageInterruptionsEnabled: source.firstMessageInterruptionsEnabled,
        },
        taskFlow: source.taskFlow ? structuredClone(source.taskFlow) : null,
      });
  };
  return (
    <Stack gap={2}>
      <Box sx={{ display: activeStep ? 'block' : 'none' }}>
        <Stack gap={2}>
          <TextField
            select
            label="Job setup"
            value={mode}
            disabled={locked}
            onChange={(event) => changeMode(event.target.value)}>
            <MenuItem value="saved">Use a saved Job</MenuItem>
            <MenuItem value="new">Create a new Job</MenuItem>
            <MenuItem value="copy" disabled={!canCopy}>
              Customize an editable copy
            </MenuItem>
          </TextField>
          {!plan ? (
            savedJobField
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                This creates a new custom Job after final review. The source Job stays unchanged.
              </Typography>
              <TextField
                label="Job title"
                value={plan.values.name}
                disabled={locked}
                inputProps={{ maxLength: 120 }}
                onChange={(event) =>
                  onPlanChange({ ...plan, values: { ...plan.values, name: event.target.value } })
                }
              />
              <TextField
                label="Conversation instructions"
                value={plan.values.prompt}
                disabled={locked}
                multiline
                minRows={5}
                onChange={(event) =>
                  onPlanChange({ ...plan, values: { ...plan.values, prompt: event.target.value } })
                }
              />
              <TextField
                label="Opening message"
                value={plan.values.firstMessage || ''}
                disabled={locked}
                multiline
                minRows={2}
                onChange={(event) =>
                  onPlanChange({
                    ...plan,
                    values: { ...plan.values, firstMessage: event.target.value },
                  })
                }
              />
              <FormControlLabel
                label="Allow interruptions during the opening message"
                control={
                  <Switch
                    disabled={locked}
                    checked={!!plan.values.firstMessageInterruptionsEnabled}
                    onChange={(_, checked) =>
                      onPlanChange({
                        ...plan,
                        values: { ...plan.values, firstMessageInterruptionsEnabled: checked },
                      })
                    }
                  />
                }
              />
              {plan.taskFlow && (
                <Alert severity="info">
                  The complete source workflow ({plan.taskFlow.tasks.length} tasks) will be copied
                  with its own identity before agent creation.
                </Alert>
              )}
            </>
          )}
          {!canCopy && source?.type === 'system' && (
            <Typography variant="caption" color="text.secondary">
              Protected system instructions can be used as saved. They cannot be copied through the
              public catalog.
            </Typography>
          )}
        </Stack>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {receipt && (
        <Alert severity={receipt.error || receipt.phase.endsWith('unknown') ? 'warning' : 'info'}>
          <Stack gap={1}>
            <Typography>
              {receipt.profile
                ? `Saved Job: ${receipt.profile.name} · ${receipt.profile.id}`
                : receipt.phase === 'job-unknown'
                  ? 'Job creation outcome unknown. It will not be replayed.'
                  : 'Creating the reviewed Job.'}
            </Typography>
            {receipt.error && <Typography>{receipt.error}</Typography>}
            {receipt.profile && !receipt.workflowSaved && (
              <Typography>
                The same Job ID is retained. Agent creation is blocked until its complete workflow
                is confirmed saved.
              </Typography>
            )}
            {receipt.phase === 'ready' && (
              <Typography>
                Job and workflow are saved. The next confirmed step creates the agent in this
                campaign.
              </Typography>
            )}
            {receipt.phase === 'completed' && (
              <Typography>
                Agent creation was confirmed in this campaign. This setup will not submit again.
                Inspect the campaign to continue.
              </Typography>
            )}
            {receipt.phase === 'agent-pending' && (
              <Typography>
                Waiting for native agent creation confirmation. The request will not be replayed.
              </Typography>
            )}
            <Stack direction="row" gap={1} flexWrap="wrap">
              {receipt.profile && (
                <Button
                  component="a"
                  href={`/workspace/jobs/${encodeURIComponent(receipt.profile.id)}`}
                  target="_blank"
                  rel="noopener noreferrer">
                  Open saved Job
                </Button>
              )}
              <Button
                component="a"
                href={`/admin/campaign/${encodeURIComponent(campaignId)}/edit`}
                target="_blank"
                rel="noopener noreferrer">
                Inspect campaign
              </Button>
              {!receipt.profile && (
                <Button
                  component="a"
                  href="/admin/assistants"
                  target="_blank"
                  rel="noopener noreferrer">
                  Inspect Job library
                </Button>
              )}
            </Stack>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
});
